import { useState } from 'react';
import { Badge, Button, Card, ConfirmModal } from '@safetyhub/ui';
import Modal from '../Modal';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../modalLayers';
import { useInvitationStore } from '../../stores/invitation';
import { useAuthStore } from '../../stores/auth';
import { OrganizationInvitation, getInvitationKindLabel } from '../../lib/organizations';
import { useToastStore } from '../../stores/toast';
import { useNotificationStore } from '../../stores/notification';
import { CalendarIcon } from '../icons';

type ConfirmAction = {
  type: 'accept' | 'reject';
  invitation: OrganizationInvitation;
} | null;

export default function InvitationModal() {
  const {
    invitations,
    isModalOpen,
    isLoading,
    closeModal,
    acceptInvitation,
    rejectInvitation,
    fetchInvitations,
  } = useInvitationStore();
  const { fetchUser } = useAuthStore();
  const { showToast } = useToastStore();
  const { addNotification } = useNotificationStore();

  const [processingToken, setProcessingToken] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const handleAcceptClick = (invitation: OrganizationInvitation) => {
    setConfirmAction({ type: 'accept', invitation });
  };

  const handleRejectClick = (invitation: OrganizationInvitation) => {
    setConfirmAction({ type: 'reject', invitation });
  };

  const { setCurrentOrganization } = useAuthStore();

  const handleConfirm = async () => {
    if (!confirmAction) return;

    const { type, invitation } = confirmAction;
    setProcessingToken(invitation.token);
    setConfirmAction(null);

    try {
      // H-8e: organization 제거 → subscription.company 또는 company 필드 사용
      const invCompanyName = invitation.subscription?.company?.name ?? invitation.company?.name ?? invitation.organization?.name ?? '';
      const invCompanyId = invitation.companyId ?? invitation.subscription?.company?.id ?? invitation.organization?.id ?? '';

      if (type === 'accept') {
        await acceptInvitation(invitation.token);
        // 토스트는 서버에서 WebSocket ACTION_RESULT로 전송됨
        // 알림 목록에 추가
        addNotification({
          type: 'invitation_accepted',
          title: '초대 수락',
          message: `'${invCompanyName}' 조직 초대를 수락했습니다.`,
          data: { companyId: invCompanyId },
        });
        await fetchUser();
        // 초대받은 조직으로 자동 전환
        if (invCompanyId) setCurrentOrganization(invCompanyId);
      } else {
        await rejectInvitation(invitation.token);
        // 토스트는 서버에서 WebSocket ACTION_RESULT로 전송됨
        // 알림 목록에 추가
        addNotification({
          type: 'invitation_rejected',
          title: '초대 거절',
          message: `'${invCompanyName}' 조직 초대를 거절했습니다.`,
          data: { companyId: invCompanyId },
        });
      }
      // 초대가 없으면 모달 닫기
      if (invitations.length <= 1) {
        closeModal();
      }
    } catch {
      showToast(type === 'accept' ? '초대 수락에 실패했습니다.' : '초대 거절에 실패했습니다.', 'error');
    } finally {
      setProcessingToken(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmAction(null);
  };

  const handleClose = () => {
    if (!processingToken) {
      closeModal();
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      title="초대 알림"
      description="대기 중인 조직/현장 초대입니다."
      size="lg"
      preventClose={!!processingToken}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={!!processingToken}>
            닫기
          </Button>
          {/* 옅은 브랜드 채움(솔리드 아님) — 톤은 유지하고 치수만 Button에 넘긴다. */}
          <Button
            variant="secondary"
            onClick={() => fetchInvitations()}
            disabled={isLoading}
            className="border-transparent bg-brand-50 text-brand-600 hover:bg-brand-100 hover:text-brand-600"
          >
            새로고침
          </Button>
        </>
      }
    >
      {isLoading && !processingToken ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : invitations.length === 0 ? (
        <div className="text-center py-8 text-ink-500">
          <BellOffIcon className="mx-auto h-12 w-12 text-ink-300" />
          <p className="mt-4">대기 중인 초대가 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {invitations.map((invitation) => (
            <InvitationCard
              key={invitation.id}
              invitation={invitation}
              onAccept={() => handleAcceptClick(invitation)}
              onReject={() => handleRejectClick(invitation)}
              isProcessing={processingToken === invitation.token}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* 확인 다이얼로그 — 초대 알림 모달(root) 위에 뜨므로 층은 여기서 sub로 주입한다.
          Esc·오버레이·✕는 취소다(종전 raw 오버레이엔 셋 다 없었다). 거절은 danger 규약이라
          자동 포커스가 「취소」에 간다 — Enter 오조작으로 초대가 거절되지 않는다. */}
      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.type === 'reject' ? '초대 거절' : '초대 수락'}
        body={
          confirmAction && (
            <p className="text-sm text-ink-600">
              <strong>{confirmAction.invitation.subscription?.company?.name ?? confirmAction.invitation.company?.name ?? confirmAction.invitation.organization?.name ?? ''}</strong>
              {confirmAction.invitation.site && (
                <> ({confirmAction.invitation.site.name})</>
              )}
              {confirmAction.type === 'accept'
                ? '의 초대를 수락하시겠습니까?'
                : '의 초대를 거절하시겠습니까?'}
            </p>
          )
        }
        confirmLabel={confirmAction?.type === 'reject' ? '거절' : '수락'}
        danger={confirmAction?.type === 'reject'}
        overlayZClassName={MODAL_LAYER.sub}
        overlayBgClassName={SUB_MODAL_OVERLAY_BG}
        onConfirm={handleConfirm}
        onClose={handleCancelConfirm}
      />

    </Modal>
  );
}

interface InvitationCardProps {
  invitation: OrganizationInvitation;
  onAccept: () => void;
  onReject: () => void;
  isProcessing: boolean;
  formatDate: (dateStr: string) => string;
}

function InvitationCard({
  invitation,
  onAccept,
  onReject,
  isProcessing,
  formatDate,
}: InvitationCardProps) {
  return (
    <Card surface="soft" elevation="none">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-ink-900 truncate">
              {invitation.subscription?.company?.name ?? invitation.company?.name ?? invitation.organization?.name ?? ''}
            </h4>
            <Badge tone="blue" shape="rounded">
              {getInvitationKindLabel(invitation)}
            </Badge>
          </div>

          {invitation.site && (
            <p className="mt-1 text-sm text-ink-600">
              <BuildingIcon className="inline h-4 w-4 mr-1" />
              {invitation.site.name}
            </p>
          )}

          <div className="mt-2 flex items-center gap-4 text-xs text-ink-500">
            <span>
              <UserIcon className="inline h-3 w-3 mr-1" />
              {invitation.inviter.name}님이 초대
            </span>
            <span>
              <CalendarIcon className="inline h-3 w-3 mr-1" />
              {formatDate(invitation.createdAt)}
            </span>
          </div>

          <p className="mt-1 text-xs text-ink-400">
            만료: {formatDate(invitation.expiresAt)}
          </p>
        </div>

        <div className="flex-shrink-0 ml-4 flex flex-col gap-2">
          <Button variant="primary" size="sm" onClick={onAccept} disabled={isProcessing}>
            {isProcessing ? '처리 중...' : '수락'}
          </Button>
          <Button variant="secondary" size="sm" onClick={onReject} disabled={isProcessing}>
            거절
          </Button>
        </div>
      </div>
    </Card>
  );
}

// 아이콘 컴포넌트들
function BellOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3l18 18"
      />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

